import React, { useState } from "react";
import cancel from "../assets/images/navbar/cancel.png";
import { CouponData } from "../services";

interface CouponListProps {
  onClose: () => void;
  coupons: CouponData[];
  loading?: boolean;
  subtotal: number;
  onApply: (coupon: CouponData) => void;
  appliedCouponCode?: string;
}

const CouponList = ({
  onClose,
  coupons,
  loading = false,
  subtotal,
  onApply,
  appliedCouponCode,
}: CouponListProps) => {
  const [couponInput, setCouponInput] = useState("");
  const [error, setError] = useState("");

  const formatDiscount = (coupon: CouponData) => {
    if (coupon.discountType === "percentage") {
      return `${coupon.discountValue}% OFF`;
    }
    return `₹${coupon.discountValue} OFF`;
  };

  const handleApplyByCode = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    const match = coupons.find((c) => c.couponCode.toUpperCase() === code);
    if (match) {
      if (subtotal < match.minOrderValue) {
        setError(`Min. order value ₹${match.minOrderValue} required`);
        return;
      }
      setError("");
      onApply(match);
      onClose();
      return;
    }
    // Allow applying codes not yet listed; pricing API will validate
    setError("");
    onApply({
      _id: `manual-${code}`,
      couponCode: code,
      description: "Applied coupon",
      discountType: "flat",
      discountValue: 0,
      maxDiscountCap: 0,
      minOrderValue: 0,
      isActive: true,
    });
    onClose();
  };

  const handleApplyCoupon = (coupon: CouponData) => {
    if (subtotal < coupon.minOrderValue) {
      setError(`Min. order value ₹${coupon.minOrderValue} required`);
      return;
    }
    setError("");
    onApply(coupon);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[1000] transition-opacity duration-300"
      onClick={onClose}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out rounded-l-[20px] shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] overflow-y-auto"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full inline-flex flex-col items-start rounded-l-[24px] bg-white dark:bg-gray-800 p-6 transition-colors duration-300">
          <div className="w-full">
            <div className="w-full flex justify-between items-center pb-[24px] border-b dark:border-gray-700">
              <p className="text-[#050505] dark:text-white font-urbanist text-[20px] font-semibold leading-[140%]">
                Your Coupons
              </p>
              <img
                src={cancel}
                alt="close"
                onClick={onClose}
                className="flex w-[36px] h-[36px] p-[7.5px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] bg-[rgba(255,255,255,0.25)] cursor-pointer"
              />
            </div>

            <div className="flex justify-between items-center px-[15px] py-[10px] rounded-[15px] bg-[#F5F5F5] dark:bg-gray-700 my-4">
              <input
                type="text"
                value={couponInput}
                onChange={(e) => {
                  setCouponInput(e.target.value.toUpperCase());
                  setError("");
                }}
                placeholder="Enter coupon code"
                className="text-[#050505] dark:text-white dark:placeholder-gray-400 font-urbanist text-[17px] leading-[160%] w-full bg-transparent outline-none pr-[15px]"
              />
              <button
                onClick={handleApplyByCode}
                className="text-white font-urbanist text-[15px] font-semibold leading-[18px] px-[15px] py-[9px] rounded-[9px] border-2 border-[#7402A7] dark:border-purple-600 bg-[#7402A7] dark:bg-purple-600 hover:bg-[#5f0289] transition-colors whitespace-nowrap"
              >
                Apply
              </button>
            </div>

            {error && (
              <p className="text-red-500 text-sm mb-3 font-urbanist">{error}</p>
            )}

            {loading ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                Loading coupons...
              </p>
            ) : coupons.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                No coupons available for this order
              </p>
            ) : (
              <div className="space-y-4">
                {coupons.map((coupon) => {
                  const isApplied =
                    appliedCouponCode?.toUpperCase() ===
                    coupon.couponCode.toUpperCase();
                  const belowMin = subtotal < coupon.minOrderValue;
                  return (
                    <div
                      key={coupon._id}
                      className="flex items-center max-w-2xl w-full mt-[12px]"
                    >
                      <div className="bg-orange-500 text-white w-[35px] h-[109px] rounded-l-2xl flex items-center justify-center">
                        <p className="rotate-[-90deg] text-[14px] font-extrabold leading-[20px] whitespace-nowrap">
                          {formatDiscount(coupon)}
                        </p>
                      </div>
                      <div className="flex-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-r-2xl px-[16px] py-[11px] flex justify-between items-center">
                        <div>
                          <div className="border-b-2 dark:border-gray-600 border-dashed">
                            <h3 className="text-[#050505] dark:text-white text-left font-urbanist text-[16px] font-medium leading-[20px]">
                              {coupon.couponCode}
                            </h3>
                            <p className="text-[#6A6A6A] dark:text-gray-400 text-left font-urbanist text-[12px] font-normal leading-[17px]">
                              {coupon.description}
                            </p>
                          </div>
                          <p className="text-[#6A6A6A] dark:text-gray-400 text-left font-urbanist text-[11px] font-normal leading-[146%] mt-3 max-w-md">
                            Min. order ₹{coupon.minOrderValue}
                            {coupon.maxDiscountCap > 0
                              ? ` · Max discount ₹${coupon.maxDiscountCap}`
                              : ""}
                          </p>
                        </div>

                        <button
                          disabled={belowMin || isApplied}
                          onClick={() => handleApplyCoupon(coupon)}
                          className={`font-urbanist text-[16px] font-semibold leading-[17px] -mt-[47px] transition-colors ${
                            isApplied
                              ? "text-green-600"
                              : belowMin
                              ? "text-gray-400 cursor-not-allowed"
                              : "text-purple-700 dark:text-purple-400"
                          }`}
                        >
                          {isApplied ? "APPLIED" : "APPLY"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponList;
